/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IMinimalEvent } from '../@types/event';
import type { Extension } from '../@types/extension';
import type { XHubChatClient } from '../client';
import type { XHubChatEvent } from '../models/event/event';
import { ClientEvent } from '../@types/event';
import { ExtensionState } from '../@types/extension';
import { User } from '../models/user/user';
import { logger } from '../utils/logger';

/**
 * Response from the server for the presence extension.
 * The keys are user IDs and the values are booleans indicating presence.
 * @example { "@user:example.com": true, "@another:example.com": false }
 */
type ExtensionPresenceResponse = Record<string, boolean>;

export type ExtensionPresenceRequest = {
  enabled: boolean;
  limit?: number;
};

type ExtensionPresenceFormat = {
  events: IMinimalEvent[];
};

export class ExtensionPresence implements Extension<ExtensionPresenceRequest, ExtensionPresenceResponse> {
  public constructor(private readonly client: XHubChatClient) {}

  public name(): string {
    return 'presence';
  }

  public when(): ExtensionState {
    return ExtensionState.PreProcess;
  }

  public async onRequest(isInitial: boolean): Promise<ExtensionPresenceRequest> {
    return {
      enabled: true,
      limit: 200, // A reasonable limit
    };
  }

  public async onResponse(data: Record<string, boolean>): Promise<void> {
    if (Object.keys(data).length === 0) return;

    const formatData: ExtensionPresenceFormat = {
      events: Object.entries(data).map(([userId, isOnline]) => {
        const presenceEvent: IMinimalEvent & { sender_id: string } = {
          type: 'm.presence',
          content: {
            presence: isOnline ? 'online' : 'offline',
            last_active_ago: 0,
            currently_active: isOnline,
            status_msg: isOnline ? 'Online' : 'Offline',
          },
          sender_id: userId,
        };
        return presenceEvent;
      }),
    };

    formatData.events
      .map(this.client.getEventMapper()) // Convert to XHubChatEvent: XHubChatEvent[]
      .forEach((presenceEvent) => {
        const senderId = presenceEvent.getSender();
        if (!senderId) return;

        const user = this.client.store.getUser(senderId) ?? User.createUser(senderId, senderId, this.client);
        user.setPresenceEvent(presenceEvent);
        this.client.store.storeUser(user);
        this.client.emit(ClientEvent.Event, presenceEvent);
      });
  }
}

type ExtensionReceiptsRequest = {
  enabled: boolean;
};

type ExtensionReceiptsResponse = {
  rooms: Record<string, XHubChatEvent>;
};

function processEphemeralEvents(client: XHubChatClient, roomId: string, ephEvents: XHubChatEvent[]): void {
  const room = client.getRoom(roomId);
  if (!room) {
    logger.warn('got ephemeral events for room but room doesn\'t exist on client:', roomId);
    return;
  }
  room.addEphemeralEvents(ephEvents);
  ephEvents.forEach((e) => {
    client.emit(ClientEvent.Event, e);
  });
}

export class ExtensionReceipts implements Extension<ExtensionReceiptsRequest, ExtensionReceiptsResponse> {
  public constructor(private readonly client: XHubChatClient) {}

  public name(): string {
    return 'receipts';
  }

  public when(): ExtensionState {
    return ExtensionState.PostProcess;
  }

  public async onRequest(isInitial: boolean): Promise<ExtensionReceiptsRequest> {
    return {
      enabled: true,
    };
  }

  public async onResponse(data: ExtensionReceiptsResponse): Promise<void> {
    if (!data?.rooms) {
      return;
    }

    for (const roomId in data.rooms) {
      processEphemeralEvents(this.client, roomId, [data.rooms[roomId]]);
    }
  }
}
